import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useSnackbarContext } from '@/data/contexts/SnackbarContext';
import i18n from '@/utils/plugins/i18n.plugin';

import { useAuth } from './useAuth';

export const useAuthWithNotifications = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { showSuccess } = useSnackbarContext();
  const hasShownWelcomeRef = useRef(false);
  const previousAuthStateRef = useRef(false);
  const isInitialMount = useRef(true);

  // Track authentication state changes
  useEffect(() => {
    const wasAuthenticated = previousAuthStateRef.current;
    const isNowAuthenticated =
      auth.isAuthenticated && auth.user?.isAuthenticated;

    console.log('🔔 Auth state change:', {
      wasAuthenticated,
      isNowAuthenticated,
      isLoading: auth.isLoading,
      hasUserData: !!(auth.user.firstName || auth.user.email),
      hasShownWelcome: hasShownWelcomeRef.current,
      isInitialMount: isInitialMount.current,
    });

    // On initial mount, if user is already authenticated (from cache), mark as shown
    if (isInitialMount.current && isNowAuthenticated && !auth.isLoading) {
      console.log(
        '✅ Initial mount with cached authenticated user, skipping notification',
      );
      hasShownWelcomeRef.current = true;
      previousAuthStateRef.current = true;
      isInitialMount.current = false;
      return;
    }

    // Mark initial mount as complete
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }

    // Show welcome message when transitioning from not authenticated to authenticated
    // (this will only happen on fresh login, not on page refresh with cached user)
    if (
      !wasAuthenticated &&
      isNowAuthenticated &&
      !auth.isLoading &&
      !hasShownWelcomeRef.current
    ) {
      // Show notification even if we don't have firstName (use email or default)
      const name =
        auth.user.firstName ||
        auth.user.fullName ||
        auth.user.email?.split('@')[0] ||
        'Utilisateur';
      console.log('🎉 Showing welcome message for:', name);

      showSuccess(t('notifications.login.success', { name }), 2000);
      hasShownWelcomeRef.current = true;
    }

    // Reset welcome flag when user logs out
    if (!isNowAuthenticated && hasShownWelcomeRef.current) {
      console.log('🚪 User logged out, resetting welcome flag');
      hasShownWelcomeRef.current = false;
    }

    // Update previous state
    previousAuthStateRef.current = isNowAuthenticated;
  }, [
    auth.isAuthenticated,
    auth.user?.isAuthenticated,
    auth.user?.firstName,
    auth.user?.fullName,
    auth.user?.email,
    auth.isLoading,
    showSuccess,
    t,
  ]);

  // Additional effect to catch delayed authentication success (only for fresh logins)
  useEffect(() => {
    // Check for authentication after successful token processing
    // Skip if initial mount (cached user) or already shown
    if (
      !isInitialMount.current &&
      auth.isAuthenticated &&
      auth.user?.isAuthenticated &&
      !hasShownWelcomeRef.current
    ) {
      // Small delay to ensure we have user data
      const timer = setTimeout(() => {
        if (!hasShownWelcomeRef.current && auth.user?.email) {
          const name =
            auth.user.firstName ||
            auth.user.fullName ||
            auth.user.email?.split('@')[0] ||
            'Utilisateur';
          console.log('⏰ Delayed welcome message for:', name);
          showSuccess(i18n.t('notifications.login.success', { name }), 2000);
          hasShownWelcomeRef.current = true;
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [
    auth.isAuthenticated,
    auth.user?.isAuthenticated,
    auth.user?.email,
    auth.user?.firstName,
    auth.user?.fullName,
    showSuccess,
  ]);

  // Enhanced login function with notification
  const loginWithNotification = () => {
    auth.login();
  };

  // Enhanced logout function with notification
  const logoutWithNotification = () => {
    const name = auth.user?.firstName || auth.user?.fullName || 'Utilisateur';
    showSuccess(i18n.t('notifications.logout.success', { name }), 3000);
    auth.logout();
  };

  return {
    ...auth,
    login: loginWithNotification,
    logout: logoutWithNotification,
  };
};
